import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import FieldSalesBandDrilldownModal from './FieldSalesBandDrilldownModal.jsx';
import {
  formatValueAuto,
  formatIndianInt,
  formatPct,
  achSlab,
  periodLabel,
} from './fieldSalesFormat.js';

// Reference tokens (SalesPulse :root + .tm-slab tints).
const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';
const GOOD = '#16A34A';
const BAD = '#DC2626';
const SHADOW = '0 1px 2px rgba(15,30,61,.04), 0 4px 18px rgba(15,30,61,.06)';

// Slab tints — faint good(green)/mid(blue)/warn(orange)/bad(red), from the HTML.
const SLAB = {
  good: { bg: 'rgba(22,163,74,.12)', color: GOOD },
  mid: { bg: 'rgba(46,124,246,.12)', color: '#2E7CF6' },
  warn: { bg: 'rgba(245,158,11,.16)', color: '#B45309' },
  bad: { bg: 'rgba(220,38,38,.12)', color: BAD },
};

// Each summary.json count key → the /summary/hqs wire selectors { group, band }.
const BAND_WIRE = {
  gt10: { group: 'growth', band: 'high' },
  '0_10': { group: 'growth', band: 'grow' },
  neg10_0: { group: 'growth', band: 'slight' },
  lt_neg10: { group: 'growth', band: 'sharp' },
  no_base: { group: 'growth', band: 'no' },
  gt100: { group: 'achievement', band: 'gt100' },
  '95_100': { group: 'achievement', band: 'b95' },
  '90_95': { group: 'achievement', band: 'b90' },
  lt90: { group: 'achievement', band: 'lt90' },
  no_target: { group: 'achievement', band: 'no' },
  gt150: { group: 'pcpm', band: 'gt150' },
  '100_150': { group: 'pcpm', band: 'b100' },
  '50_100': { group: 'pcpm', band: 'b50' },
  lt50: { group: 'pcpm', band: 'lt50' },
};

/** Sum the bucket counts of a counts object (hq_counts or tm_counts). */
function sumCounts(counts) {
  return Object.values(counts ?? {}).reduce(
    (acc, v) => acc + (Number.isFinite(Number(v)) ? Number(v) : 0),
    0
  );
}

/** Per-card HQ/TM radio pill (reference .basis-rg). */
function BasisToggle({ value, onChange, name }) {
  const Opt = ({ v }) => {
    const active = value === v;
    return (
      <Box
        component="label"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          fontSize: '10.5px',
          fontWeight: 600,
          letterSpacing: '.3px',
          textTransform: 'uppercase',
          cursor: 'pointer',
          color: active ? '#2E7CF6' : MUTED,
        }}
      >
        <input
          type="radio"
          name={name}
          value={v}
          checked={active}
          onChange={() => onChange(v)}
          style={{ margin: 0, width: 11, height: 11, cursor: 'pointer' }}
        />
        <b>{v}</b>
      </Box>
    );
  };
  return (
    <Box
      title="Count bands by HQs or TMs"
      sx={{
        display: 'inline-flex',
        gap: '7px',
        bgcolor: '#f3f6fb',
        border: `1px solid ${BORDER}`,
        borderRadius: '999px',
        px: '7px',
        py: '2px',
        flex: '0 0 auto',
      }}
    >
      <Opt v="HQ" />
      <Opt v="TM" />
    </Box>
  );
}

/** One slab tile. `clickable` adds the ⤢ expand glyph + pointer. */
function Slab({ n, label, tone, clickable, onClick, ariaLabel }) {
  const palette = SLAB[tone] ?? SLAB.mid;
  return (
    <Box
      component={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      aria-label={ariaLabel}
      sx={{
        position: 'relative',
        border: '1px solid transparent',
        borderRadius: '8px',
        p: '7px 6px',
        textAlign: 'center',
        bgcolor: palette.bg,
        color: palette.color,
        font: 'inherit',
        width: '100%',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'transform .08s, box-shadow .08s',
        ...(clickable
          ? {
              '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(15,30,61,.14)' },
              '&:after': {
                content: '"⤢"',
                position: 'absolute',
                top: '3px',
                right: '5px',
                fontSize: '9px',
                opacity: 0.5,
              },
            }
          : {}),
      }}
    >
      <Box sx={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.1 }}>{formatIndianInt(n)}</Box>
      <Box sx={{ fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '.3px', mt: '1px', opacity: 0.85 }}>
        {label}
      </Box>
    </Box>
  );
}

/** Strip of 4 slabs + optional footer link (reference slabStrip). */
function SlabStrip({ cells, foot, footOnClick, title, columns = 4 }) {
  return (
    <>
      <Box
        title={title}
        sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: '6px', mt: '10px' }}
      >
        {cells.map(({ key, ...cell }) => (
          <Slab key={key} {...cell} />
        ))}
      </Box>
      {foot ? (
        <Box
          component={footOnClick ? 'button' : 'div'}
          type={footOnClick ? 'button' : undefined}
          onClick={footOnClick}
          sx={{
            display: 'block',
            width: '100%',
            background: 'none',
            border: 0,
            font: 'inherit',
            textAlign: 'center',
            fontSize: '10.5px',
            color: MUTED,
            mt: '6px',
            cursor: footOnClick ? 'pointer' : 'default',
            textDecoration: footOnClick ? 'underline' : 'none',
            textUnderlineOffset: '2px',
            '&:hover': footOnClick ? { color: '#2E7CF6' } : undefined,
          }}
        >
          {foot}
        </Box>
      ) : null}
    </>
  );
}

/** KPI card shell (reference .kpi-card). */
function KpiCard({ label, basis, value, delta, deltaText, children }) {
  const deltaColor = delta === 'up' ? GOOD : delta === 'down' ? BAD : MUTED;
  const arrow = delta === 'up' ? '▲ ' : delta === 'down' ? '▼ ' : delta === 'flat' ? '→ ' : '';
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: SHADOW,
        p: '14px 16px',
        // Faint coloured top line (reference .kpi-card:before).
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg,#2E7CF6,#7db0ff)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 18 }}>
        <Typography
          sx={{ fontSize: '11px', color: MUTED, textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600 }}
        >
          {label}
        </Typography>
        {basis}
      </Box>
      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: NAVY, mt: '4px' }}>{value}</Typography>
      {deltaText ? (
        <Typography sx={{ fontSize: '12px', mt: '3px', color: deltaColor }}>
          {arrow}
          {deltaText}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

/**
 * Field Sales — the four KPI cards (reference .kpi-row). Lives at PAGE level,
 * above the tabs, so it stays visible on every tab. Sales w/ Target+Achievement
 * tiles; Growth YoY; Spread of Performance w/ SOP%; PCPM/TM. Each band card has
 * its own HQ/TM toggle (display-only). Clicking a band / footer link opens the
 * drill-down modal (/summary/hqs, group+band).
 *
 * @param {{ summary: object }} props  mapped getSummary data (card1-4, months, fiscalYear)
 */
export default function FieldSalesKpiCards({ summary }) {
  const [growthBasis, setGrowthBasis] = useState('HQ');
  const [spreadBasis, setSpreadBasis] = useState('HQ');
  const [pcpmBasis, setPcpmBasis] = useState('HQ');
  const [drill, setDrill] = useState(null);

  const card1 = summary?.card1 ?? {};
  const card2 = summary?.card2 ?? {};
  const card3 = summary?.card3 ?? {};
  const card4 = summary?.card4 ?? {};

  const pl = periodLabel(summary?.months);
  const fyLabel = summary?.fiscalYear ? `Year: ${summary.fiscalYear}` : '';

  const countsOf = (card, basis) => (basis === 'TM' ? card.tm_counts : card.hq_counts) ?? {};
  const growthCounts = countsOf(card2, growthBasis);
  const spreadCounts = countsOf(card3, spreadBasis);
  const pcpmCounts = countsOf(card4, pcpmBasis);

  const openDrill = (countKey, count, basis) => {
    const wire = BAND_WIRE[countKey];
    if (!wire) return;
    setDrill({
      descriptor: {
        group: wire.group,
        band: wire.band,
        pl,
        count: Number.isFinite(Number(count)) ? Number(count) : 0,
        unit: basis === 'TM' ? 'TMs' : 'HQs',
      },
    });
  };

  const ach = card1.ach;
  const salesTiles = [
    { key: 'target', label: `${pl} Target`, tone: 'mid', render: formatValueAuto(card1.target) },
    {
      key: 'ach',
      label: 'Achievement',
      tone: achSlab(ach),
      render: Number.isFinite(Number(ach)) ? `${Number(ach).toFixed(1)}%` : '—',
    },
  ];

  const growthWord = (w) => (growthBasis === 'TM' ? 'TMs' : 'HQs') + ' ' + w;
  const growthTiles = [
    { key: 'gt10', n: growthCounts.gt10, label: '>+10%', tone: 'good' },
    { key: '0_10', n: growthCounts['0_10'], label: '0–10%', tone: 'mid' },
    { key: 'neg10_0', n: growthCounts.neg10_0, label: '-10–0%', tone: 'warn' },
    { key: 'lt_neg10', n: growthCounts.lt_neg10, label: '<-10%', tone: 'bad' },
  ].map((t) => ({
    ...t,
    clickable: true,
    onClick: () => openDrill(t.key, t.n, growthBasis),
    ariaLabel: `Growth ${t.label}: ${formatIndianInt(t.n)}`,
  }));
  const noBase = growthCounts.no_base;

  const spreadWord = spreadBasis === 'TM' ? 'TMs' : 'HQs';
  const spreadTotal = sumCounts(spreadCounts);
  const sop = card3.sop_pct;
  const achTiles = [
    { key: 'gt100', n: spreadCounts.gt100, label: '>100%', tone: 'good' },
    { key: '95_100', n: spreadCounts['95_100'], label: '95–100%', tone: 'mid' },
    { key: '90_95', n: spreadCounts['90_95'], label: '90–95%', tone: 'warn' },
    { key: 'lt90', n: spreadCounts.lt90, label: '<90%', tone: 'bad' },
  ].map((t) => ({
    ...t,
    clickable: true,
    onClick: () => openDrill(t.key, t.n, spreadBasis),
    ariaLabel: `Achievement ${t.label}: ${formatIndianInt(t.n)}`,
  }));
  const noTarget = spreadCounts.no_target;

  const pcpmTmTotal = sumCounts(card4.tm_counts);
  const pcpmTiles = [
    { key: 'gt150', n: pcpmCounts.gt150, label: '>150%', tone: 'good' },
    { key: '100_150', n: pcpmCounts['100_150'], label: '100–150%', tone: 'mid' },
    { key: '50_100', n: pcpmCounts['50_100'], label: '50–100%', tone: 'warn' },
    { key: 'lt50', n: pcpmCounts.lt50, label: '<50%', tone: 'bad' },
  ].map((t) => ({
    ...t,
    clickable: true,
    onClick: () => openDrill(t.key, t.n, pcpmBasis),
    ariaLabel: `PCPM/TM ${t.label}: ${formatIndianInt(t.n)}`,
  }));

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', lg: 'repeat(4,1fr)' },
          gap: '14px',
          mb: '16px',
        }}
      >
        {/* Card 1 — Sales */}
        <KpiCard
          label={`${pl} Sales (FY26-27)`}
          value={Number(card1.sales) > 0 ? formatValueAuto(card1.sales) : '—'}
          delta="flat"
          deltaText={fyLabel}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '6px', mt: '10px' }}>
            {salesTiles.map((t) => {
              const palette = SLAB[t.tone] ?? SLAB.mid;
              return (
                <Box
                  key={t.key}
                  sx={{
                    borderRadius: '8px',
                    p: '7px 6px',
                    textAlign: 'center',
                    bgcolor: palette.bg,
                    color: palette.color,
                    border: '1px solid transparent',
                  }}
                >
                  <Box sx={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.1 }}>{t.render}</Box>
                  <Box
                    sx={{
                      fontSize: '9.5px',
                      textTransform: 'uppercase',
                      letterSpacing: '.3px',
                      mt: '1px',
                      opacity: 0.85,
                    }}
                  >
                    {t.label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </KpiCard>

        {/* Card 2 — Growth */}
        <KpiCard
          label={`Growth — ${pl} YoY`}
          basis={<BasisToggle name="fs-basis-growth" value={growthBasis} onChange={setGrowthBasis} />}
          value={formatPct(card2.growth, true)}
          delta={
            Number.isFinite(Number(card2.growth))
              ? Number(card2.growth) > 0
                ? 'up'
                : Number(card2.growth) < 0
                ? 'down'
                : 'flat'
              : 'flat'
          }
          deltaText={
            Number.isFinite(Number(card2.ly_sales)) && Number.isFinite(Number(card1.sales))
              ? `${pl} LY ${formatValueAuto(card2.ly_sales)} → CY ${formatValueAuto(card1.sales)}`
              : ''
          }
        >
          <SlabStrip
            cells={growthTiles}
            foot={noBase ? `+ ${formatIndianInt(noBase)} ${growthWord('without prior-year base')}` : ''}
            footOnClick={noBase ? () => openDrill('no_base', noBase, growthBasis) : undefined}
            title="Click a band to list its HQs"
          />
        </KpiCard>

        {/* Card 3 — Spread of Performance */}
        <KpiCard
          label="Spread of Performance"
          basis={<BasisToggle name="fs-basis-spread" value={spreadBasis} onChange={setSpreadBasis} />}
          value={`${formatIndianInt(spreadTotal)} ${spreadBasis === 'TM' ? 'TM' : 'HQ'}`}
          delta="flat"
          deltaText={Number.isFinite(Number(sop)) ? `${pl} - SOP ${Number(sop).toFixed(0)}% (TMs ≥100%)` : '—'}
        >
          <SlabStrip
            cells={achTiles}
            foot={noTarget ? `+ ${formatIndianInt(noTarget)} ${spreadWord} without ${pl} target` : ''}
            footOnClick={noTarget ? () => openDrill('no_target', noTarget, spreadBasis) : undefined}
            title="Click a band to list its HQs"
          />
        </KpiCard>

        {/* Card 4 — PCPM/TM */}
        <KpiCard
          label={`PCPM / TM — ${pl}`}
          basis={<BasisToggle name="fs-basis-pcpm" value={pcpmBasis} onChange={setPcpmBasis} />}
          value={Number.isFinite(Number(card4.pcpm_tm)) ? formatValueAuto(card4.pcpm_tm) : '—'}
          delta="flat"
          deltaText={`Sales / 1 mo · ${formatIndianInt(pcpmTmTotal)} TM · per territory manager`}
        >
          <SlabStrip cells={pcpmTiles} title="Click a band to list its HQs (by PCPM/TM)" />
        </KpiCard>
      </Box>

      <FieldSalesBandDrilldownModal
        open={Boolean(drill)}
        onClose={() => setDrill(null)}
        descriptor={drill?.descriptor ?? null}
      />
    </>
  );
}
